import {
  Box,
  Button,
  CircularProgress,
  Stack,
  styled,
  Typography,
  useTheme,
} from '@mui/material';
import { captureException } from '@sentry/react';
import { useAnimate } from 'framer-motion';
import { useCallback, useState } from 'react';
import { useEffectOnce } from 'react-use';

import { preferenceClient } from '@portal-clients/preference';
import { OnboardingPreferencesModel } from '@portal-clients/user';

import {
  LargeNotificationIcon,
  useToast,
  ViewSection,
} from '@frontend/components';
import { tApp } from '@frontend/locales';
import { usePushNotifications } from '@frontend/notifications';
import { useUser } from '@frontend/utils';

import { useOnboarding } from '../../use-onboarding';

const BellButton = styled('button')(({ theme }) => ({
  background: 'transparent',
  outline: 'none',
  border: 'none',
  cursor: 'pointer',
  paddingTop: theme.spacing(1.5),
  paddingBottom: theme.spacing(1.5),

  // Disable blue highlight on tap in Android
  '-webkit-tap-highlight-color': 'rgba(255, 255, 255, 0)',
}));

export const PushNotificationStep = () => {
  const theme = useTheme();
  const { stepsPerformed } = useOnboarding();
  const { isTurningOnOrOff, requestPermission, turnOn } =
    usePushNotifications();
  const { user, updatePreferences } = useUser();
  const { showToast } = useToast();
  const [applying, setApplying] = useState(false);

  const setOnboardingStepAsPerformed = useCallback(async () => {
    const preference: OnboardingPreferencesModel = {
      // For backwards compatibility we keed old version number
      onboarded: true,
      version: 1,
      stepsPerformed: [...stepsPerformed, 'PUSH_NOTIFICATIONS'],
    };

    await preferenceClient
      .set('onboarding', {
        preference,
      })
      .then(() => {
        updatePreferences({
          onboarding: preference,
        });
      })
      .catch((e) => {
        captureException(e, {
          level: 'warning',
          extra: {
            message: 'Failed to update preferences for onboarding',
          },
        });
      });
  }, [stepsPerformed, updatePreferences]);

  const handleEnableNotifications = useCallback(async () => {
    await requestPermission()
      .then(async (granted) => {
        if (granted) {
          await turnOn();
        }
      })
      .then(setOnboardingStepAsPerformed)
      .catch((error) => {
        console.error(
          'Error requesting and turning on push notifications:',
          error,
        );
        captureException(error);

        showToast({
          severity: 'error',
          body: tApp.pushNotifications.errorEnabling(),
        });
      });
  }, []);

  const [scope, animate] = useAnimate();

  const startBellAnimation = useCallback(() => {
    animate(
      scope.current,
      {
        rotate: [0, 0, -3, 5, 0, -5, 3, 0],
        scale: [1, 1.1, 1.1, 1.1, 1.1, 1.1, 1.1, 1],
      },
      { repeat: 1, duration: 0.7, ease: 'easeInOut' },
    );
  }, [scope, animate]);

  useEffectOnce(() => {
    startBellAnimation();
  });

  return (
    <Stack spacing={3} pt={2} pb={2}>
      <ViewSection>
        <Stack alignItems="center" spacing={1} pt={6} pb={2}>
          <BellButton type="button" ref={scope} onClick={startBellAnimation}>
            <LargeNotificationIcon
              stroke={theme.palette.primary.main}
              style={{ fontSize: 210 }}
            />
          </BellButton>

          <Typography variant="h3">{tApp.pushNotifications.title()}</Typography>

          <Typography
            variant="body1"
            color="textSecondary"
            align="center"
            width={327}
          >
            {tApp.pushNotifications.descriptionBody1({
              operatorDisplayName: user?.operator.displayName ?? '',
            })}
          </Typography>

          <Typography variant="body1" color="textSecondary" align="center">
            {tApp.pushNotifications.descriptionBody2()}
          </Typography>
        </Stack>
      </ViewSection>

      <ViewSection>
        <Stack spacing={2}>
          {applying && (
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <CircularProgress size="large" />
            </Box>
          )}

          {!applying && (
            <>
              <Button
                loading={isTurningOnOrOff}
                variant="contained"
                size="large"
                onClick={() => {
                  setApplying(true);
                  handleEnableNotifications().finally(() => {
                    setApplying(false);
                  });
                }}
              >
                {tApp.pushNotifications.enable()}
              </Button>
              <Button
                variant="text"
                size="large"
                onClick={() => {
                  setApplying(true);
                  setOnboardingStepAsPerformed().finally(() => {
                    setApplying(false);
                  });
                }}
              >
                {tApp.pushNotifications.maybeLater()}
              </Button>
            </>
          )}
        </Stack>
      </ViewSection>
    </Stack>
  );
};
